import { decodeJwtPayload } from '../utils/jwt';
import { EnvironmentService } from '../services/environment-service';
import { ModalRemoteParty } from './modal-remote-party';
const TOKEN_STORAGE_KEY = 'synapse-token';
export class DefaultUiFrameAdapter {
    constructor() {
        var _a, _b;
        this.signatureKeyChangedSubscriptions = [];
        this.confirmModalSubscriptions = [];
        this.focusRestorationTarget = null;
        this.companyId = '';
        this.userId = '';
        /** @internal */
        this.isAdmin = false;
        /** @internal */
        this.iss = '';
        // Turn accessTokenFactory into a promise factory, and bind it, so that we still allow other teams to continue using it like this:
        // let {accessTokenFactory} = uiFrameAdapter;
        // accessTokenFactory();
        this.accessTokenFactory = async () => this.getTokenFromLocalStorage();
        this.clientComponent = this.determineClientComponent();
        const payload = this.parseToken(this.getTokenFromLocalStorage());
        this.companyId = payload.companyId;
        this.userId = payload.userId;
        this.iss = payload.iss;
        this.isAdmin = payload.isAdmin;
        window.addEventListener('synapse-frame-event', event => {
            const signatureKeyEnteredEventData = event.detail.zeroTrustKey;
            if (signatureKeyEnteredEventData) {
                this.onSignatureKeyChanged(signatureKeyEnteredEventData);
            }
            const confirmModalResult = event.detail.confirmModalResult;
            if (confirmModalResult) {
                this.onConfirmModalResult(confirmModalResult);
            }
        });
        const { confirmModalMoved, firstTimeFlowModalMoved, signatureKeyModalMoved, waitingForApprovalModalMoved } = ((_b = (_a = this.options) === null || _a === void 0 ? void 0 : _a.__zeroTrustLib) === null || _b === void 0 ? void 0 : _b.featureFlagOverrides) || {};
        if (confirmModalMoved || firstTimeFlowModalMoved || signatureKeyModalMoved || waitingForApprovalModalMoved) {
            this.warmModalFrame().catch(console.error); // no good logging facilites at this point
        }
    }
    get options() {
        var _a;
        const rawOptions = (_a = document.querySelector('[options]')) === null || _a === void 0 ? void 0 : _a.getAttribute('options');
        return (rawOptions ? JSON.parse(rawOptions) : {});
    }
    async triggerFirstTimeFlow() {
        var _a, _b, _c;
        if ((_c = (_b = (_a = this.options) === null || _a === void 0 ? void 0 : _a.__zeroTrustLib) === null || _b === void 0 ? void 0 : _b.featureFlagOverrides) === null || _c === void 0 ? void 0 : _c.firstTimeFlowModalMoved) {
            this.saveFocus();
            const party = await ModalRemoteParty.Create(await this.getModalUrl());
            await party.doModalSession(async (sendRequest) => {
                const response = await sendRequest({
                    zeroTrustModalRequest: {
                        type: 'startFirstTimeFlow',
                        params: {
                            jwt: this.getTokenFromLocalStorage()
                        },
                        settings: this.determineModalSettings(),
                    }
                });
                if ('error' in response) {
                    throw new Error(response.error);
                }
                else {
                    if (response.result) {
                        const returnUrl = `${window.top.location.pathname}${window.top.location.search}${window.top.location.hash}`;
                        const abortUrl = `${window.top.location.pathname}${window.top.location.search}${window.top.location.hash}`;
                        this.navigateToApp('zero-trust', `?returnUrl=${encodeURIComponent(returnUrl)}&abortUrl=${encodeURIComponent(abortUrl)}`);
                    }
                }
                this.restoreFocus();
            });
        }
        else {
            this.notifyUiFrame({ type: 'zero-trust-first-time-modal-triggered', exposed: true });
        }
    }
    async triggerSignatureKeyModal(modalText, errorCode, askFrequency) {
        var _a, _b, _c;
        if ((_c = (_b = (_a = this.options) === null || _a === void 0 ? void 0 : _a.__zeroTrustLib) === null || _b === void 0 ? void 0 : _b.featureFlagOverrides) === null || _c === void 0 ? void 0 : _c.signatureKeyModalMoved) {
            this.saveFocus();
            const party = await ModalRemoteParty.Create(await this.getModalUrl());
            return await party.doModalSession(async (sendRequest) => {
                const response = await sendRequest({
                    zeroTrustModalRequest: {
                        type: 'signatureKey',
                        params: {
                            content: modalText,
                            errorCode: errorCode,
                            askFrequency: askFrequency,
                        },
                        settings: this.determineModalSettings(),
                    }
                });
                if ('error' in response) {
                    throw new Error(response.error);
                }
                else {
                    this.restoreFocus();
                    switch (response.type) {
                        case 'confirm':
                            return response.result;
                        case 'cancel':
                            return { password: null, askFrequency: 'perTask' };
                        case 'forgotKey':
                            this.navigateToApp('zero-trust', '?flow=forgotPassword');
                            return { password: null, askFrequency: 'perTask' };
                        default:
                            return { password: null, askFrequency: 'perTask' };
                    }
                }
            });
        }
        else {
            this.saveFocus();
            this.notifyUiFrame({ type: 'zero-trust-signature-key-modal-triggered', exposed: true, modalText, errorCode, askFrequency });
            return new Promise(resolve => {
                const callback = (data) => {
                    this.signatureKeyChangedSubscriptions = this.signatureKeyChangedSubscriptions.filter(item => item !== callback);
                    this.restoreFocus();
                    resolve(data);
                };
                this.signatureKeyChangedSubscriptions.push(callback);
            });
        }
    }
    async triggerWaitingForApprovalModal() {
        var _a, _b, _c;
        if ((_c = (_b = (_a = this.options) === null || _a === void 0 ? void 0 : _a.__zeroTrustLib) === null || _b === void 0 ? void 0 : _b.featureFlagOverrides) === null || _c === void 0 ? void 0 : _c.waitingForApprovalModalMoved) {
            this.saveFocus();
            const party = await ModalRemoteParty.Create(await this.getModalUrl());
            await party.doModalSession(async (sendRequest) => {
                const response = await sendRequest({
                    zeroTrustModalRequest: {
                        type: 'waitingForApproval',
                        params: {
                            jwt: this.getTokenFromLocalStorage()
                        },
                        settings: this.determineModalSettings(),
                    }
                });
                if ('error' in response) {
                    throw new Error(response.error);
                }
                else {
                    switch (response.type) {
                        case 'cancel':
                            this.navigateToApp('zero-trust', '/getting-started');
                            break;
                        default:
                            this.restoreFocus();
                            break;
                    }
                }
            });
        }
        else {
            this.notifyUiFrame({ type: 'zero-trust-waiting-approval-modal-triggered', exposed: true });
        }
    }
    async triggerConfirmModal(options) {
        var _a, _b, _c;
        if ((_c = (_b = (_a = this.options) === null || _a === void 0 ? void 0 : _a.__zeroTrustLib) === null || _b === void 0 ? void 0 : _b.featureFlagOverrides) === null || _c === void 0 ? void 0 : _c.confirmModalMoved) {
            this.saveFocus();
            const party = await ModalRemoteParty.Create(await this.getModalUrl());
            return await party.doModalSession(async (sendRequest) => {
                const response = await sendRequest({
                    zeroTrustModalRequest: {
                        type: 'confirm',
                        params: {
                            title: options.modalTitle,
                            content: options.modalText,
                            confirmButton: options.modalConfirmButton,
                        },
                        settings: this.determineModalSettings(),
                    }
                });
                if ('error' in response) {
                    throw new Error(response.error);
                }
                else {
                    this.restoreFocus();
                    return { confirmed: response.result };
                }
            });
        }
        else {
            this.saveFocus();
            const nonce = Math.random().toString().substring(2, 8);
            this.notifyUiFrame(Object.assign(Object.assign({ type: 'zero-trust-confirm-modal-triggered', exposed: true }, options), { nonce }));
            return new Promise(resolve => {
                const callback = (data) => {
                    if (data.nonce !== nonce) {
                        return;
                    }
                    this.restoreFocus();
                    this.confirmModalSubscriptions = this.confirmModalSubscriptions.filter(item => item !== callback);
                    resolve(data);
                };
                this.confirmModalSubscriptions.push(callback);
            });
        }
    }
    navigate(route) {
        this.notifyUiFrame({ type: 'navigated', route });
    }
    navigateToApp(prefix, route = '') {
        this.notifyUiFrame({ type: 'navigated', prefix, route });
    }
    // Shows the toaster in the UI frame designed way, with a 5 seconds timeOut at the top-center.
    addToast(message, messageType) {
        this.notifyUiFrame({
            type: 'add-toast',
            message,
            messageType,
            exposed: true,
        });
    }
    onSignatureKeyChanged(signatureKeyData) {
        for (const subscriber of this.signatureKeyChangedSubscriptions) {
            subscriber(signatureKeyData);
        }
    }
    onConfirmModalResult(result) {
        for (const subscriber of this.confirmModalSubscriptions) {
            subscriber(result);
        }
    }
    notifyUiFrame(detail) {
        window.top.dispatchEvent(new CustomEvent('synapse-app-event', {
            detail,
            bubbles: true,
            composed: true
        }));
    }
    saveFocus() {
        this.focusRestorationTarget = document.activeElement;
    }
    determineClientComponent() {
        if (window === top) {
            return 'UiFrame/Frontend';
        }
        else {
            const element = document.querySelector('[options]');
            if (!element) {
                throw new Error('Could not determine clientComponent');
            }
            // Convert tag to the AppName/Frontend format, that was in use by convention before the componentName parameter got removed from the constructor of this class,
            // Example conversion: "REMOTE-EXECUTION" tagName -> "RemoteExecution/Frontend"
            const tagName = element.tagName;
            return tagName.toLowerCase().split('-').map(([firstLetter, ...rest]) => firstLetter.toUpperCase() + rest.join('')).join('') + '/Frontend';
        }
    }
    determineTheme() {
        var _a;
        const element = document.querySelector('[options]');
        return ((_a = element === null || element === void 0 ? void 0 : element.getAttribute('theme')) === null || _a === void 0 ? void 0 : _a.endsWith('dark')) ? 'dark' : 'light';
    }
    determineLang() {
        var _a;
        const element = document.querySelector('[options]');
        return (_a = element === null || element === void 0 ? void 0 : element.getAttribute('language')) !== null && _a !== void 0 ? _a : 'en-EN';
    }
    determineModalSettings() {
        return {
            lang: this.determineLang(),
            look: {
                skin: 'gotoresolve',
                theme: this.determineTheme(),
            }
        };
    }
    parseToken(token) {
        const payload = decodeJwtPayload(token);
        return {
            companyId: payload.companyId,
            userId: payload.sub,
            iss: payload.iss,
            isAdmin: payload.capabilities.some(c => c === 'admin')
        };
    }
    restoreFocus() {
        if (this.focusRestorationTarget && this.focusRestorationTarget.focus) {
            try {
                this.focusRestorationTarget.focus({ preventScroll: true });
            }
            catch (_a) {
                // focus() really shouldn't throw, but let's be on the safe-side as this is in a critical path
            }
            finally {
                this.focusRestorationTarget = null;
            }
        }
    }
    getTokenFromLocalStorage() {
        const token = localStorage[TOKEN_STORAGE_KEY];
        if (!token) {
            throw new Error('JWT token is missing');
        }
        return token;
    }
    async warmModalFrame() {
        if (window.requestIdleCallback) {
            window.requestIdleCallback(() => {
                this.getModalUrl().then(modalUrl => ModalRemoteParty.Create(modalUrl)).catch(console.error); // no good logging facilites at this point
            }, { timeout: 2500 });
        }
        else {
            // Safari does not support requestIdleCallback
            setTimeout(() => {
                this.getModalUrl().then(modalUrl => ModalRemoteParty.Create(modalUrl)).catch(console.error); // no good logging facilites at this point
            }, 500);
        }
    }
    async getModalUrl() {
        var _a, _b;
        const environmentService = new EnvironmentService(this);
        const environment = await environmentService.determineEnvironment();
        const urls = {
            local: 'https://zerotrust-modal.services-dev01.gotoresolve.com',
            dev: 'https://zerotrust-modal.services-dev01.gotoresolve.com',
            stage: 'https://zerotrust-modal.services-stage.gotoresolve.com',
            prodeu: 'https://zerotrust-modal-eu.services.gotoresolve.com',
            prod: 'https://zerotrust-modal.services.gotoresolve.com',
        };
        return (_b = (_a = this.options.__zeroTrustLib) === null || _a === void 0 ? void 0 : _a.modalUrl) !== null && _b !== void 0 ? _b : urls[environment];
    }
}
